import React, { useState, useCallback, useRef } from "react";
import { Button, Dropdown, Input } from "antd";
import { FaPlus } from "react-icons/fa";
import { BCC_KEYWORD_TYPES, IBCCKeyword } from "@ts/common-types";

interface IAddCustomKeywordButtonProps {
    onAdd: (keyword: IBCCKeyword) => void;
    keywords: IBCCKeyword[];
    modalParams: any;
    tableConfig: any;
    tableContainerRef?: React.RefObject<HTMLDivElement>;
}

const AddCustomKeywordButton: React.FC<IAddCustomKeywordButtonProps> = ({
    onAdd,
    keywords,
    modalParams,
    tableConfig,
    tableContainerRef,
}) => {
    const [customKeywordInput, setCustomKeywordInput] = useState("");
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [keywordError, setKeywordError] = useState("");
    const customKeywordInputRef = useRef<any>(null);

    const handleDropdownOpenChange = useCallback(
        (open: boolean) => {
            if (open) {
                setDropdownOpen(true);
                setTimeout(() => {
                    customKeywordInputRef?.current?.focus();
                }, 100);
            } else {
                setDropdownOpen(false);
                setCustomKeywordInput("");
                setKeywordError("");
            }
        },
        [dropdownOpen, customKeywordInputRef]
    );

    const handleAddClick = useCallback(() => {
        const trimmedKeyword = customKeywordInput.trim();
        if (!trimmedKeyword) return;

        const { clusterId, collectionId } = modalParams || {};
        if (!clusterId || !collectionId) return;

        // Check if keyword already exists (case-insensitive)
        const keywordExists = keywords?.some((kw: IBCCKeyword) => {
            return kw.keyword?.toLowerCase() === trimmedKeyword.toLowerCase();
        });

        if (keywordExists) {
            setKeywordError("This keyword already exists");
            return;
        }

        // Clear any previous errors
        setKeywordError("");

        // Generate custom keyword object from table column definitions
        const customKeyword: IBCCKeyword = {} as IBCCKeyword;

        // Extract dataIndex from each column definition to build the object structure
        tableConfig?.colDefs?.forEach((col: any) => {
            if (!col.dataIndex) return;

            const dataIndex = col.dataIndex;

            switch (dataIndex) {
                case "keyword":
                    customKeyword[dataIndex] = trimmedKeyword;
                    break;
                case "keyword_type":
                    customKeyword[dataIndex] = BCC_KEYWORD_TYPES.CUSTOM;
                    break;
                default:
                    if (dataIndex.includes("_change")) {
                        customKeyword[dataIndex] = 0;
                    } else {
                        customKeyword[dataIndex] = null;
                    }
                    break;
            }
        });

        // Add metadata fields
        customKeyword.key = `${clusterId}||${collectionId}||${trimmedKeyword}||custom`;
        customKeyword.clusterId = clusterId;
        customKeyword.id = collectionId;
        customKeyword.isCustom = true;

        // Add to context state
        onAdd(customKeyword);

        //! NOTE: This has to be changed during ag-grid refactoring
        if (tableContainerRef) {
            setTimeout(() => {
                const container = tableContainerRef.current;
                if (container) {
                    const virtualHolder = container.querySelector(
                        ".ant-table-tbody-virtual-holder"
                    ) as HTMLElement;
                    if (virtualHolder) {
                        virtualHolder.scrollTop = 0;
                    }
                }
            }, 150);
        }

        setCustomKeywordInput("");
        setDropdownOpen(false);
    }, [customKeywordInput, modalParams, onAdd, tableConfig, keywords, tableContainerRef]);

    return (
        <Dropdown
            trigger={["click"]}
            open={dropdownOpen}
            onOpenChange={handleDropdownOpenChange}
            menu={{
                items: [],
            }}
            popupRender={() => (
                <div className="campaign-creation-custom-keyword-popup">
                    <div className="title">Add Custom Keyword</div>
                    <div className="mt-4">
                        <Input
                            ref={customKeywordInputRef}
                            placeholder="Custom keyword"
                            value={customKeywordInput}
                            onChange={(e) => {
                                setCustomKeywordInput(e.target.value);
                                setKeywordError("");
                            }}
                            onPressEnter={handleAddClick}
                            status={keywordError ? "error" : ""}
                        />
                        {keywordError && (
                            <div className="error-message">{keywordError}</div>
                        )}
                    </div>
                    <div className="d-flex justify-content-start gap-2 mt-4">
                        <Button
                            type="primary"
                            onClick={handleAddClick}
                            disabled={!customKeywordInput.trim()}
                        >
                            Add
                        </Button>
                        <Button
                            onClick={() => {
                                setCustomKeywordInput("");
                                setKeywordError("");
                                setDropdownOpen(false);
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            )}
            placement="bottom"
        >
            <Button type="primary" icon={<FaPlus />} iconPosition="start">
                Add Custom Keyword
            </Button>
        </Dropdown>
    );
};

export default AddCustomKeywordButton;

